import React, { useContext, useEffect, useState, useRef } from 'react'
// import './listing.scss'
import './nice-select.css'
import { ShoppingContext } from '../../shared/ShoppingContext'
import { axios } from '../../utils/api'
import '../Content/content.scss'
import 'swiper/swiper-bundle.min.css'
import { none } from 'ramda'
import { mixpanelLogEvent } from '../../utils/mixpanelLogEvent'

const getCategoryId = async (id, cancelToken) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/shopping/category/categoryName?id=${id}`,
      { cancelToken },
    )
    return response.data.name
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Request cancelled')
    } else {
      console.error(`Error: ${error}`)
    }
  }
}

const facetOptionRenamer = async (facetName, option) => {
  if (facetName === 'categories') {
    const translatedOption = await getCategoryId(Number(option))
    return translatedOption
  } else if (facetName === 'isInStock') {
    const stock = option === 'true' ? 'In-Stock' : 'Out-of-Stock'
    return stock
  } else {
    return option
  }
}

function capitalizeFirstLetterOfWords(str) {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const AccordionPanel = ({
  facet,
  option,
  facetsForFiltering,
  setFacetsForFiltering,
  searchTerm,
  categoryLabel,
}) => {
  const [optionName, setOptionName] = useState('')
  const [loading, setLoading] = useState(false)
  const { userInfo } = useContext(ShoppingContext)

  const logClickEvent = ({ eventType, facetName, optionName }) => {
    mixpanelLogEvent({
      eventType,
      userDetails: userInfo,
      metaData: {
        searchTerm,
        categoryLabel,
        facetName,
        optionName,
      },
    })
  }

  useEffect(() => {
    let isMounted = true // flag to determine if the component is still mounted

    const fetchOptionName = async () => {
      setLoading(true)

      try {
        const name = await facetOptionRenamer(facet.name, option.name)
        if (isMounted) {
          setOptionName(name)
          setLoading(false)
        }
      } catch (error) {
        if (isMounted) {
          // handle error, for example:
          console.error('Error fetching option name:', error)
          setLoading(false)
        }
      }
    }

    fetchOptionName()

    // Cleanup function
    return () => {
      isMounted = false // set it to false when component unmounts
    }
  }, [facet.name, option.name])

  const addOptionToArray = option => {
    logClickEvent({
      eventType: `PLP_FacetSidebar_Option_Click`,
      facetName: facet.name,
      optionName: option.name,
    })
    const newSelectedOptions = { ...facetsForFiltering }
    if (!newSelectedOptions[facet.name]) {
      newSelectedOptions[facet.name] = []
    }
    if (newSelectedOptions[facet.name].includes(option.name)) {
      newSelectedOptions[facet.name] = newSelectedOptions[facet.name].filter(
        opt => opt !== option.name,
      )
    } else {
      newSelectedOptions[facet.name].push(option.name)
    }
    setFacetsForFiltering(newSelectedOptions)
  }

  const isChecked =
    facetsForFiltering[facet.name] && facetsForFiltering[facet.name].includes(option.name)

  return (
    <>
      {!loading && (
        <li className="listing__sidebar-block-item">
          {option.values && (
            <input
              type="checkbox"
              className="listing__sidebar-block-checkboxField"
              checked={isChecked}
              onChange={() => addOptionToArray(option)}
            />
          )}
          <label className="listing__sidebar-block-checkboxLabel">
            {optionName} {option.values && `(${option.values})`}
          </label>
        </li>
      )}
    </>
  )
}

function FacetAccordion({
  facet,
  index,
  activeBlocks,
  setActiveBlocks,
  facetsForFiltering,
  setFacetsForFiltering,
  searchTerm,
  categoryLabel,
}) {
  const [canSeeMore, setCanSeeMore] = useState(false)
  function renameFacetName(name) {
    const renameMapping = {
      isInStock: 'Availability',
      'RX Level value': 'Rx Level',
    }

    return renameMapping[name] || name
  }

  const isActive = activeBlocks.includes(index)
  const valuesArray = Object.entries(facet.values).map(([name, values]) => ({ name, values }))
  const modifiedValuesArray = valuesArray
    .filter(value => {
      if (facet.name === 'isInStock') {
        return value.name === 'true'
      }
      return true
    })
    .map(value => ({ name: value.name, values: value.values }))

  const sortedValuesArray = modifiedValuesArray.sort((a, b) => {
    return b.values - a.values
  })

  const handleClick = () => {
    if (isActive) {
      setActiveBlocks(activeBlocks.filter(activeIndex => activeIndex !== index))
    } else {
      setActiveBlocks([...activeBlocks, index])
    }
  }

  const valuesToShow = canSeeMore ? sortedValuesArray : sortedValuesArray.slice(0, 5)
  return (
    <div className="listing__sidebar-block">
      <div
        className={`listing__sidebar-block-indicator ${isActive ? 'active' : ''}`}
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
      ></div>
      <h5
        className={`listing__sidebar-block-heading ${isActive ? 'active' : ''}`}
        onClick={handleClick}
      >
        {capitalizeFirstLetterOfWords(renameFacetName(facet.name))}
      </h5>
      <ul className="listing__sidebar-block-list">
        {valuesToShow.map((option, index) => (
          <AccordionPanel
            option={option}
            key={index}
            facetsForFiltering={facetsForFiltering}
            setFacetsForFiltering={setFacetsForFiltering}
            facet={facet}
            searchTerm={searchTerm}
            categoryLabel={categoryLabel}
          />
        ))}
        {!canSeeMore && sortedValuesArray.length > 5 && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ cursor: 'pointer' }} onClick={() => setCanSeeMore(true)}>
              see more
            </p>
          </div>
        )}
      </ul>
    </div>
  )
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

export const FacetSidebar = ({
  facets,
  products,
  productsData,
  siblingCategories,
  searchTerm,
  categoryLabel,
  resetFacetsAndProducts,
}) => {
  const facetsArray = facets && Object.entries(facets).map(([name, values]) => ({ name, values }))

  const sortOrder = ['isInStock', 'manufacturer', 'categories', 'RX Level value'] //Order for facets at the top of the list

  const facetsArraySorted = facetsArray.sort((a, b) => {
    const indexA = sortOrder.indexOf(a.name)
    const indexB = sortOrder.indexOf(b.name)

    // If a is in sortOrder but b is not, a comes first
    if (indexA !== -1 && indexB === -1) {
      return -1
    }

    // If b is in sortOrder but a is not, b comes first
    if (indexA === -1 && indexB !== -1) {
      return 1
    }

    // If both a and b are in sortOrder, sort based on order in array
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB
    }

    // If neither a nor b are in sortOrder, maintain their order
    return 0
  })

  const [activeBlocks, setActiveBlocks] = useState(['isInStock', 'manufacturer', 'categories'])
  const { facetsForFiltering, setFacetsForFiltering } = useContext(ShoppingContext)

  useEffect(() => {
    const newActiveBlocks = facetsArraySorted
      .map((facet, index) =>
        facetsForFiltering[facet.name] && facetsForFiltering[facet.name].length > 0
          ? facet.name
          : -1,
      )
      .filter(index => index !== -1)

    const defaultActiveBlocks = ['isInStock', 'manufacturer', 'categories']
    setActiveBlocks([...new Set([...defaultActiveBlocks, ...newActiveBlocks])])
  }, [facetsForFiltering])

  const { width } = useWindowDimensions()
  const [isOpenFacet, setOpenFacet] = useState(width > 768 ? true : false)
  useEffect(() => {
    setOpenFacet(width > 768)
  }, [width])

  return (
    <div className="page-holder listing">
      <div className="listing__wrapper">
        <div className="listing__sidebar">
          <div className="listing__sidebar-top">
            <p className="listing__sidebar-top-text">{productsData?.total} items found</p>
            <button
              className="listing__sidebar-top-button"
              onClick={() => {
                setFacetsForFiltering({})
                resetFacetsAndProducts()
              }}
            >
              Reset
            </button>
          </div>

          <div className="listing__sidebar-filter">
            <div className="listing__sidebar-filter-title-container">
              <p
                className="listing__sidebar-filter-title"
                style={width > 768 ? { display: 'none' } : { display: 'flex' }}
              >
                See filters
              </p>

              <p
                className="listing__sidebar-filter-title-icon"
                style={width > 768 ? { display: 'none' } : { display: 'flex' }}
                onClick={() => setOpenFacet(!isOpenFacet)}
              ></p>
            </div>
            <div
              className="listing__sidebar-filter-categories"
              style={isOpenFacet ? { display: 'block' } : { display: 'none' }}
            >
              {facetsArraySorted &&
                facetsArraySorted.map((facet, index) => (
                  <FacetAccordion
                    key={index}
                    facet={facet}
                    index={facet.name}
                    activeBlocks={activeBlocks}
                    setActiveBlocks={setActiveBlocks}
                    facetsForFiltering={facetsForFiltering}
                    setFacetsForFiltering={setFacetsForFiltering}
                    searchTerm={searchTerm}
                    categoryLabel={categoryLabel}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
